import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Edit,
  Trash2,
  X,
  RefreshCw,
  CalendarDays,
  Users,
  LogIn,
  MessageCircle,
  Mail,
  Link as LinkIcon,
  FileText,
  CheckCircle2,
  DollarSign,
  TriangleAlert,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';
import AppointmentFormModal from '../components/appointments/AppointmentFormModal';
import { buildAppointmentConflictCounts } from '../utils/appointmentConflicts';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);
const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const CALENDAR_VIEW_VALUES = new Set(['month', 'week', 'day', 'agenda']);

const STATUS = [
  { value: 'Agendado', label: 'Agendado', color: '#3B82F6' },
  { value: 'Pendente de confirmação', label: 'Pendente de confirmação', color: '#8B5CF6' },
  { value: 'Confirmado', label: 'Confirmado', color: '#0E7490' },
  { value: 'Realizado', label: 'Realizado', color: '#15803D' },
  { value: 'Cancelado', label: 'Cancelado', color: '#EF4444' },
  { value: 'Não Compareceu', label: 'Não Compareceu', color: '#F59E0B' }
];

const CHECK_IN_ALLOWED_STATUSES = new Set(['Agendado', 'Confirmado']);
const canStartAppointment = (appointment) =>
  CHECK_IN_ALLOWED_STATUSES.has(appointment?.status);

const TERMINAL_APPOINTMENT_STATUSES = new Set([
  'Realizado',
  'Não Compareceu',
  'Cancelado',
]);
const canUseOperationalActions = (appointment) =>
  !TERMINAL_APPOINTMENT_STATUSES.has(appointment?.status);

const getStatusColor = (status) => {
  const s = STATUS.find(x => x.value === status);
  return s ? s.color : '#6B7280';
};

const formatDateTimeBR = (value) => {
  if (!value) return '';
  const parsed = moment.utc(value).local();
  if (!parsed.isValid()) return '';
  return parsed.format('DD/MM/YYYY HH:mm');
};

const formatCurrencyBR = (value) => {
  const parsed = Number(value ?? 0);
  const safeValue = Number.isFinite(parsed) ? parsed : 0;
  return safeValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const normalizeFinancialStage = (value) =>
  String(value || 'forecast')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_');

const FINANCIAL_STAGE_RANK = {
  forecast: 1,
  predicted: 1,
  previsto: 1,
  produced: 2,
  produzido: 2,
  invoiced: 3,
  faturado: 3,
  partially_received: 4,
  partial_received: 4,
  parcialmente_recebido: 4,
  received: 5,
  recebido: 5,
};

const getFinancialStageRank = (appointment) => {
  const normalized = normalizeFinancialStage(appointment?.financial_stage);
  const mappedRank = FINANCIAL_STAGE_RANK[normalized] || 0;

  if (mappedRank) return mappedRank;
  if (Number(appointment?.received_value || 0) > 0) return 4;
  if (appointment?.invoice_id || Number(appointment?.invoiced_value || 0) > 0) return 3;
  if (Number(appointment?.produced_value || 0) > 0) return 2;
  if (appointment?.push_to_finance === true) return 1;
  return 0;
};

const getFinancialStatusText = (appointment) => {
  const normalized = normalizeFinancialStage(appointment?.financial_stage);
  const received = Number(appointment?.received_value || 0);
  const invoiced = Number(appointment?.invoiced_value || 0);

  if (
    normalized === 'received' ||
    normalized === 'recebido' ||
    (invoiced > 0 && received >= invoiced)
  ) {
    return 'Recebimento concluído';
  }

  if (
    normalized === 'partially_received' ||
    normalized === 'partial_received' ||
    normalized === 'parcialmente_recebido' ||
    received > 0
  ) {
    return 'Recebimento parcial';
  }

  if (appointment?.invoice_id || normalized === 'invoiced' || normalized === 'faturado') {
    return 'Faturado — aguardando pagamento';
  }

  if (normalized === 'produced' || normalized === 'produzido') {
    return 'Produção concluída — aguardando faturamento';
  }

  return 'Previsão financeira criada';
};

const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const timeToMinutes = (value) => {
  if (!value || typeof value !== 'string' || !value.includes(':')) return null;
  const [h, m] = value.split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const minutesToDate = (baseDate, minutes) => {
  const safeMinutes = Math.max(0, Math.min(1439, minutes));
  return moment(baseDate)
    .startOf('day')
    .hour(Math.floor(safeMinutes / 60))
    .minute(safeMinutes % 60)
    .second(0)
    .millisecond(0)
    .toDate();
};

function getQS(name) {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  return url.searchParams.get(name);
}

const AgendaPage = () => {
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = useCallback((permission) => permissions.has(permission), [permissions]);
  const { toast, showToast } = useToast();

  const [appointments, setAppointments] = useState([]);
  const [appointmentBlocks, setAppointmentBlocks] = useState([]);
  const [partnerActivities, setPartnerActivities] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    try {
      const storedContext = localStorage.getItem('agendaReturnContext');
      if (!storedContext) return;

      const context = JSON.parse(storedContext);
      if (CALENDAR_VIEW_VALUES.has(context?.view)) {
        setCurrentView(context.view);
      }
      const contextDate = moment(context?.date, 'YYYY-MM-DD', true);
      if (contextDate.isValid()) {
        setCurrentDate(contextDate.toDate());
      }
      localStorage.removeItem('agendaReturnContext');
    } catch (error) {
      console.warn('Não foi possível restaurar o ponto anterior da agenda:', error);
      localStorage.removeItem('agendaReturnContext');
    }
  }, []);

  const agendaViewStorageKey = useMemo(() => {
    const userIdentifier = user?.id || user?.user_id || user?.email;
    return userIdentifier ? `odontoassist:agenda:view:${userIdentifier}` : null;
  }, [user?.id, user?.user_id, user?.email]);

  const agendaDateStorageKey = useMemo(() => {
    const userIdentifier = user?.id || user?.user_id || user?.email;
    return userIdentifier ? `odontoassist:agenda:date:${userIdentifier}` : null;
  }, [user?.id, user?.user_id, user?.email]);

  useEffect(() => {
    if (!agendaViewStorageKey) return;

    try {
      const savedView = localStorage.getItem(agendaViewStorageKey);
      if (CALENDAR_VIEW_VALUES.has(savedView)) {
        setCurrentView(savedView);
      }
    } catch (error) {
      console.warn('Não foi possível restaurar a visualização da agenda:', error);
    }
  }, [agendaViewStorageKey]);

  useEffect(() => {
    if (!agendaDateStorageKey) return;

    try {
      const savedDate = localStorage.getItem(agendaDateStorageKey);
      const parsedDate = savedDate ? moment(savedDate, 'YYYY-MM-DD', true) : null;
      if (parsedDate?.isValid()) {
        setCurrentDate(parsedDate.toDate());
      }
    } catch (error) {
      console.warn('Não foi possível restaurar a data da agenda:', error);
    }
  }, [agendaDateStorageKey]);

  const handleCalendarViewChange = useCallback((view) => {
    if (!CALENDAR_VIEW_VALUES.has(view)) return;

    setCurrentView(view);

    if (!agendaViewStorageKey) return;
    try {
      localStorage.setItem(agendaViewStorageKey, view);
    } catch (error) {
      console.warn('Não foi possível salvar a visualização da agenda:', error);
    }
  }, [agendaViewStorageKey]);

  const handleCalendarNavigate = useCallback((date) => {
    setCurrentDate(date);

    if (!agendaDateStorageKey || !date) return;
    try {
      localStorage.setItem(
        agendaDateStorageKey,
        moment(date).format('YYYY-MM-DD')
      );
    } catch (error) {
      console.warn('Não foi possível salvar a data da agenda:', error);
    }
  }, [agendaDateStorageKey]);

  const [professionalFilter, setProfessionalFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [showAdditionalAgendaActions, setShowAdditionalAgendaActions] = useState(false);

  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFinancialCompletionModal, setShowFinancialCompletionModal] = useState(false);
  const [completingFinancial, setCompletingFinancial] = useState(false);
  const [financialCompletionForm, setFinancialCompletionForm] = useState({
    amount: '',
    discount_amount: '',
    receive_now: true,
    payment_method_id: 'pix',
    payment_method_name: 'PIX',
    payment_date: moment().format('YYYY-MM-DD'),
    due_date: moment().format('YYYY-MM-DD'),
    payment_type: 'single',
    installment_count: 2,
    notes: '',
  });
  const [registeringPayment, setRegisteringPayment] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method_id: 'pix',
    payment_method_name: 'PIX',
    payment_date: moment().format('YYYY-MM-DD'),
    notes: '',
  });

  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [loadingWhatsappLogs, setLoadingWhatsappLogs] = useState(false);
  const [showWhatsappMessagesModal, setShowWhatsappMessagesModal] = useState(false);

  const [showOnlineBookingWhatsappModal, setShowOnlineBookingWhatsappModal] = useState(false);
  const [sendingOnlineBookingWhatsApp, setSendingOnlineBookingWhatsApp] = useState(false);
  const [onlineBookingWhatsappMode, setOnlineBookingWhatsappMode] = useState('registered');
  const [onlineBookingPatientId, setOnlineBookingPatientId] = useState('');
  const [onlineBookingGuestName, setOnlineBookingGuestName] = useState('');
  const [onlineBookingGuestPhone, setOnlineBookingGuestPhone] = useState('');

  // modal compartilhado
  const [showForm, setShowForm] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm, setBlockForm] = useState({
    block_mode: 'time',
    appointment_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    professional_id: '',
    professional_name: '',
    reason: '',
  });
  const [editing, setEditing] = useState(null); // objeto do agendamento em edição
  const [presetDateTime, setPresetDateTime] = useState({ date: '', time: '' });

  const hasWhatsAppModule =
    clinicSettings?.whatsapp_module_access_active === true ||
    clinicSettings?.whatsapp_module_status === 'active' ||
    clinicSettings?.whatsapp_module_status === 'courtesy' ||
    clinicSettings?.whatsapp_module_status === 'cancel_pending' ||
    clinicSettings?.whatsapp_module_enabled === true ||
    user?.whatsapp_module_enabled === true ||
    user?.whatsapp_module_courtesy === true;

  const canSendOnlineBookingWhatsapp =
    hasWhatsAppModule &&
    clinicSettings?.appointment_whatsapp_enabled === true &&
    clinicSettings?.online_booking_enabled === true &&
    !!clinicSettings?.online_booking_token;

  // NOVO: paciente vindo por contexto (extensão / URL)
  const [selectedPatientContext, setSelectedPatientContext] = useState(null);
  const [handledUrlContext, setHandledUrlContext] = useState(false);

  useEffect(() => {
    let mounted = true;

    const authHeaders = () => ({
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const fetchAll = async () => {
      // A Agenda deve aparecer assim que os agendamentos chegarem. Dados usados
      // apenas em filtros e formulários continuam carregando em paralelo e não
      // bloqueiam mais a renderização do calendário.
      const auxiliaryRequests = Promise.allSettled([
        can('clinical_records.view') ? axios.get(`${API_URL}/treatments`, authHeaders()) : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/patients/lookup`, authHeaders()),
        axios.get(`${API_URL}/clinic-settings`, authHeaders()),
        axios.get(`${API_URL}/appointment-blocks`, authHeaders()),
        can('billing.view') ? axios.get(`${API_URL}/billing/partner-activities/calendar`, authHeaders()) : Promise.resolve({ data: [] })
      ]);

      try {
        const appsRes = await axios.get(`${API_URL}/appointments`, authHeaders());

        if (!mounted) return;

        const listA = Array.isArray(appsRes.data) ? appsRes.data : (appsRes.data?.items || []);
        setAppointments(listA);
      } catch (e) {
        console.error('Erro ao carregar dados da agenda:', e);
        toast?.error('Erro ao carregar agenda');
      } finally {
        if (mounted) setLoading(false);
      }

      const [treatsResult, patsResult, settingsResult, blocksResult, partnerActivitiesResult] = await auxiliaryRequests;
      if (!mounted) return;

      if (treatsResult.status === 'fulfilled') {
        const data = treatsResult.value.data;
        setTreatments(Array.isArray(data) ? data : (data?.items || []));
      } else {
        console.warn('Não foi possível carregar os tratamentos auxiliares da agenda:', treatsResult.reason);
      }

      if (patsResult.status === 'fulfilled') {
        const data = patsResult.value.data;
        setPatients(Array.isArray(data) ? data : (data?.items || []));
      } else {
        console.warn('Não foi possível carregar a lista simplificada de pacientes:', patsResult.reason);
      }

      if (settingsResult.status === 'fulfilled') {
        setClinicSettings(settingsResult.value.data || null);
      } else {
        console.warn('Não foi possível carregar as configurações auxiliares da agenda:', settingsResult.reason);
      }

      if (blocksResult.status === 'fulfilled') {
        setAppointmentBlocks(Array.isArray(blocksResult.value.data) ? blocksResult.value.data : []);
      } else {
        console.warn('Não foi possível carregar os bloqueios auxiliares da agenda:', blocksResult.reason);
      }

      if (partnerActivitiesResult.status === 'fulfilled') {
        setPartnerActivities(Array.isArray(partnerActivitiesResult.value.data) ? partnerActivitiesResult.value.data : []);
      } else {
        console.warn('Não foi possível carregar as atividades comerciais auxiliares da agenda:', partnerActivitiesResult.reason);
      }
    };

    const reloadAppointmentsOnly = async () => {
      try {
        const appsRes = await axios.get(`${API_URL}/appointments`, authHeaders());
        const listA = Array.isArray(appsRes.data) ? appsRes.data : (appsRes.data?.items || []);
        if (mounted) setAppointments(listA);
      } catch (e) {
        console.error('Erro ao recarregar agendamentos:', e);
      }
    };

    const reloadBlocksOnly = async () => {
      try {
        const blocksRes = await axios.get(`${API_URL}/appointment-blocks`, authHeaders());
        if (mounted) {
          setAppointmentBlocks(Array.isArray(blocksRes.data) ? blocksRes.data : []);
        }
      } catch (e) {
        console.error('Erro ao recarregar bloqueios:', e);
      }
    };

    fetchAll();
    const handler = () => reloadAppointmentsOnly();
    window.addEventListener('appointments:changed', handler);

    const blockHandler = () => reloadBlocksOnly();
    window.addEventListener('appointment-blocks:changed', blockHandler);    

    return () => {
      mounted = false;
      window.removeEventListener('appointments:changed', handler);
      window.removeEventListener('appointment-blocks:changed', blockHandler);
    };
  }, [toast, can]);

  // NOVO: abre a agenda com paciente pré-selecionado via URL
  useEffect(() => {
    if (loading || handledUrlContext || !patients.length) return;

    const patientId = getQS('patientId') || getQS('patient_id');
    const shouldOpenNew = getQS('new') === '1';

    if (!patientId) {
      setHandledUrlContext(true);
      return;
    }

    const patient = patients.find((p) => p.id === patientId);
    if (!patient) {
      setHandledUrlContext(true);
      return;
    }

    setSelectedPatientContext({
      id: patient.id,
      full_name: patient.full_name
    });

    if (shouldOpenNew) {
      const dateQS = getQS('date') || '';
      const timeQS = getQS('time') || '';

      const treatmentIdQS =
        getQS('treatmentId') ||
        getQS('treatment_id') ||
        '';

      const alignerTreatmentIdQS =
        getQS('alignerTreatmentId') ||
        getQS('aligner_treatment_id') ||
        '';

      const professionalIdQS =
        getQS('professionalId') ||
        getQS('professional_id') ||
        '';

      const linkedTreatment = treatments.find(
        (treatment) =>
          treatment.id === treatmentIdQS &&
          treatment.patient_id === patientId
      );

      const professionals =
        clinicSettings?.professionals || [];

      const linkedProfessional = professionals.find(
        (professional) =>
          professional.id === professionalIdQS
      );

      setEditing({
        patient_id: patient.id,
        patient_name: patient.full_name,

        appointment_date: dateQS,
        start_time: timeQS,

        treatment_id:
          linkedTreatment?.id ||
          treatmentIdQS ||
          '',

        aligner_treatment_id:
          alignerTreatmentIdQS || '',

        procedure:
          linkedTreatment?.procedure ||
          linkedTreatment?.name ||
          linkedTreatment?.title ||
          'Acompanhamento de tratamento com alinhadores',

        professional_id:
          linkedProfessional?.id ||
          professionalIdQS ||
          '',

        professional_name:
          linkedProfessional?.name ||
          '',

        amount: '',

        push_to_finance: false,
      });

      setPresetDateTime({
        date: dateQS,
        time: timeQS,
      });

      setShowForm(true);
    }

    setHandledUrlContext(true);
  }, [
    loading,
    handledUrlContext,
    patients,
    treatments,
    clinicSettings,
  ]);

  const filteredAppointments = appointments.filter((apt) => {
    if (
      apt.source === 'aligner_event' ||
      apt.source === 'clinical_evolution_manual' ||
      apt.import_category === 'clinical_evolution' ||
      apt.is_clinical_evolution === true ||
      apt.procedure === 'Evolução Clínica Manual' ||
      apt.calendar_visible === false
    ) {
      return false;
    }

    if (professionalFilter !== 'all' && apt.professional_id !== professionalFilter) {
      return false;
    }

    if (originFilter === 'manual' && apt.booking_source === 'online') {
      return false;
    }

    if (originFilter === 'online' && apt.booking_source !== 'online') {
      return false;
    }

    if (originFilter === 'blocks' || originFilter === 'commercial') {
      return false;
    }

    return true;
  });

  const filteredBlocks = appointmentBlocks.filter((block) => {
    if (professionalFilter !== 'all' && block.professional_id !== professionalFilter) {
      return false;
    }

    if (originFilter !== 'all' && originFilter !== 'blocks') {
      return false;
    }

    return true;
  });

  const blockEvents = filteredBlocks.map((block) => {
    const startDateTime = moment(`${block.appointment_date} ${block.start_time}`, 'YYYY-MM-DD HH:mm').toDate();
    const endDateTime = block.end_time
      ? moment(`${block.appointment_date} ${block.end_time}`, 'YYYY-MM-DD HH:mm').toDate()
      : moment(startDateTime).add(30, 'minutes').toDate();

    return {
      id: block.id,
      title: `Bloqueado${block.reason ? ` - ${block.reason}` : ''}`,
      start: startDateTime,
      end: endDateTime,
      resource: {
        ...block,
        is_block: true,
      },
      style: {
        backgroundColor: '#6b7280',
        borderColor: '#4b5563',
        color: 'white',
        opacity: 0.85,
      },
    };
  });

  const conflictCounts = buildAppointmentConflictCounts(
    filteredAppointments,
    Number(clinicSettings?.appointment_duration || 60)
  );

  const calendarEvents = filteredAppointments.map(apt => {
    const startDateTime = moment(`${apt.appointment_date} ${apt.start_time}`, 'YYYY-MM-DD HH:mm').toDate();
    const endDateTime = apt.end_time
      ? moment(`${apt.appointment_date} ${apt.end_time}`, 'YYYY-MM-DD HH:mm').toDate()
      : moment(startDateTime).add(1, 'hour').toDate();

    const color = apt.booking_source === 'online'
      ? getStatusColor(apt.status) || '#8b5cf6'
      : getStatusColor(apt.status);

    const conflictCount = conflictCounts.get(apt.id) || 0;

    return {
      id: apt.id,
      title: apt.is_scope_private ? 'Horário ocupado' : `${apt.patient_name} - ${apt.procedure}`,
      start: startDateTime,
      end: endDateTime,
      resource: { ...apt, conflict_count: conflictCount },
      conflictCount,
      style: {
        backgroundColor: apt.is_scope_private ? '#6b7280' : color,
        borderColor: conflictCount > 0 ? '#f59e0b' : (apt.is_scope_private ? '#4b5563' : color),
        borderWidth: conflictCount > 0 ? '3px' : '1px',
        color: 'white',
      }
    };
  });

  const CalendarEvent = ({ event }) => (
    <div
      className="flex min-w-0 items-center gap-1"
      title={event.conflictCount > 0
        ? `Conflito de horário: ${event.conflictCount} agendamento(s) sobreposto(s)`
        : event.title}
    >
      {event.conflictCount > 0 && <TriangleAlert className="h-3.5 w-3.5 flex-none" aria-label="Conflito de horário" />}
      <span className="truncate">{event.title}</span>
      {event.conflictCount > 0 && (
        <span className="ml-auto flex-none rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
          Conflito
        </span>
      )}
    </div>
  );

  const commercialEvents = (partnerActivities || [])
    .filter(() => originFilter === 'all' || originFilter === 'commercial')
    .map((activity) => {
      const start = moment.utc(activity.start_datetime).local();
      const end = activity.end_datetime
        ? moment.utc(activity.end_datetime).local()
        : start.clone().add(1, 'hour');
      const typeLabels = { CALL: 'Ligação', WHATSAPP: 'WhatsApp', EMAIL: 'E-mail', MEETING: 'Reunião', VISIT: 'Visita', TRAINING: 'Treinamento', DEMO: 'Demonstração', FOLLOW_UP: 'Follow-up', TASK: 'Tarefa', MATERIAL_DELIVERY: 'Entrega de material', OTHER: 'Atividade' };
      return {
        id: `partner-activity-${activity.id}`,
        title: `${typeLabels[activity.activity_type] || 'Atividade'} — ${activity.partner_name || 'Parceiro'}: ${activity.title}`,
        start: start.toDate(),
        end: end.toDate(),
        resource: { ...activity, is_partner_activity: true },
        style: { backgroundColor: '#7c3aed', borderColor: '#6d28d9', color: 'white' },
      };
    });

  const allCalendarEvents = [...calendarEvents, ...blockEvents, ...commercialEvents];

  const calendarTimeRange = useMemo(() => {
    const fallbackMin = 8 * 60;
    const fallbackMax = 18 * 60;

    if (!['week', 'day'].includes(currentView)) {
      return {
        min: minutesToDate(currentDate, fallbackMin),
        max: minutesToDate(currentDate, fallbackMax),
      };
    }

    const visibleStart =
      currentView === 'week'
        ? moment(currentDate).startOf('week')
        : moment(currentDate).startOf('day');

    const visibleEnd =
      currentView === 'week'
        ? moment(currentDate).endOf('week')
        : moment(currentDate).endOf('day');

    let minMinutes = null;
    let maxMinutes = null;

    const applyRange = (start, end) => {
      if (start == null || end == null || end <= start) return;

      minMinutes = minMinutes == null ? start : Math.min(minMinutes, start);
      maxMinutes = maxMinutes == null ? end : Math.max(maxMinutes, end);
    };

    const workingHours = clinicSettings?.working_hours || {};

    let cursor = visibleStart.clone();
    while (cursor.isSameOrBefore(visibleEnd, 'day')) {
      const dayKey = DAY_KEYS[cursor.day()];
      const config = workingHours?.[dayKey];

      if (config?.enabled !== false) {
        applyRange(
          timeToMinutes(config?.start),
          timeToMinutes(config?.end)
        );
      }

      cursor.add(1, 'day');
    }

    filteredAppointments.forEach((apt) => {
      const aptDate = moment(apt.appointment_date, 'YYYY-MM-DD');
      if (!aptDate.isValid()) return;
      if (aptDate.isBefore(visibleStart, 'day') || aptDate.isAfter(visibleEnd, 'day')) return;
      if (apt.status === 'Cancelado') return;

      const start = timeToMinutes(apt.start_time);
      const end =
        timeToMinutes(apt.end_time) ??
        (start != null ? start + 60 : null);

      applyRange(start, end);
    });

    if (minMinutes == null) minMinutes = fallbackMin;
    if (maxMinutes == null) maxMinutes = fallbackMax;

    if (maxMinutes <= minMinutes) {
      maxMinutes = Math.min(1439, minMinutes + 60);
    }

    return {
      min: minutesToDate(currentDate, minMinutes),
      max: minutesToDate(currentDate, maxMinutes),
    };
  }, [clinicSettings?.working_hours, currentDate, currentView, filteredAppointments]);

  const handleSelectEvent = useCallback((event) => {
    if (event?.resource?.is_scope_private) {
      showToast('Horário ocupado por outro profissional.', 'info');
      return;
    }
    setSelectedEvent(event);
    setShowEventModal(true);
  }, [showToast]);

  const handleSelectSlot = useCallback((slotInfo) => {
    const date = moment(slotInfo.start).format('YYYY-MM-DD');
    const time = moment(slotInfo.start).format('HH:mm');
    setEditing(null);
    setSelectedPatientContext(null);
    setPresetDateTime({ date, time });
    setShowForm(true);
  }, []);

  const messages = {
    allDay: 'Dia todo',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Não há eventos neste período.',
    showMore: (total) => `+ Ver mais (${total})`
  };

  const onSavedFromModal = () => {
    // O próprio modal dispara appointments:changed
  };

  const refreshAgenda = async () => {
    try {
      setLoading(true);

      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      };

      const [appsRes, blocksRes, partnerActivitiesRes] = await Promise.all([
        axios.get(`${API_URL}/appointments`, authHeaders),
        axios.get(`${API_URL}/appointment-blocks`, authHeaders),
        axios.get(`${API_URL}/billing/partner-activities/calendar`, authHeaders)
      ]);

      setAppointments(
        Array.isArray(appsRes.data)
          ? appsRes.data
          : (appsRes.data?.items || [])
      );

      setAppointmentBlocks(
        Array.isArray(blocksRes.data)
          ? blocksRes.data
          : []
      );

      setPartnerActivities(
        Array.isArray(partnerActivitiesRes.data)
          ? partnerActivitiesRes.data
          : []
      );

      toast?.success('Agenda atualizada.');
    } catch (error) {
      console.error(error);
      toast?.error('Erro ao atualizar agenda.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este agendamento?')) return;
    try {
      await axios.delete(`${API_URL}/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Agendamento excluído com sucesso!', 'success');
      window.dispatchEvent(new CustomEvent('appointments:changed'));
      setShowEventModal(false);
    } catch (error) {
      console.error('Erro ao excluir agendamento:', error);
      showToast(
        error?.response?.status === 403
          ? 'Você não tem permissão para excluir agendamentos. Altere o status para Cancelado.'
          : (error?.response?.data?.detail || 'Erro ao excluir agendamento'),
        'error'
      );
    }
  };

  const loadWhatsappMessages = async () => {
    if (!selectedEvent?.resource?.id) return;

    try {
      setLoadingWhatsappLogs(true);

      const response = await axios.get(
        `${API_URL}/appointments/${selectedEvent.resource.id}/whatsapp-messages`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      setWhatsappLogs(Array.isArray(response.data) ? response.data : []);
      setShowWhatsappMessagesModal(true);
    } catch (error) {
      console.error('Erro ao carregar mensagens WhatsApp:', error);
      toast?.error('Erro ao carregar mensagens enviadas.');
    } finally {
      setLoadingWhatsappLogs(false);
    }
  };

  const handleSendConfirmationEmail = async () => {
    if (!selectedEvent?.resource?.id) return;

    const apt = selectedEvent.resource;

    if (!canUseOperationalActions(apt)) {
      toast?.error('Este agendamento está encerrado e não permite mensagens operacionais.');
      return;
    }

    if (!window.confirm(`Enviar e-mail de confirmação para ${apt.patient_name}?`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/appointments/${apt.id}/send-confirmation-email`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast?.success(response.data?.message || 'E-mail enviado com sucesso!');

      setSelectedEvent({
        ...selectedEvent,
        resource: {
          ...apt,
          appointment_confirmation_email_sent: true,
          appointment_confirmation_email_sent_at: new Date().toISOString(),
        }
      });

      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao enviar e-mail de confirmação:', error);
      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar e-mail de confirmação.';
      toast?.error(msg);
      window.alert(msg);
    }
  };

  const handleSendConfirmationWhatsApp = async () => {
    if (!selectedEvent?.resource?.id) return;

    const apt = selectedEvent.resource;

    if (!canUseOperationalActions(apt)) {
      toast?.error('Este agendamento está encerrado e não permite mensagens operacionais.');
      return;
    }

    const alreadySent = apt.appointment_confirmation_whatsapp_sent === true;

    const confirmMessage = alreadySent
      ? `Já existe registro de WhatsApp enviado para ${apt.patient_name}.\n\nDeseja reenviar mesmo assim?`
      : `Enviar WhatsApp de confirmação para ${apt.patient_name}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/appointments/${apt.id}/send-confirmation-whatsapp`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast?.success(response.data?.message || 'WhatsApp enviado com sucesso!');

      setSelectedEvent({
        ...selectedEvent,
        resource: {
          ...apt,
          appointment_confirmation_whatsapp_sent: true,
          appointment_confirmation_whatsapp_sent_at: new Date().toISOString(),
        }
      });

      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao enviar WhatsApp de confirmação:', error);
      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar WhatsApp de confirmação.';
      toast?.error(msg);
      window.alert(msg);
    }
  };

  const handleSendRescheduleWhatsApp = async () => {
    if (!selectedEvent?.resource?.id) return;

    const apt = selectedEvent.resource;

    if (!canUseOperationalActions(apt)) {
      toast?.error('Este agendamento está encerrado e não permite mensagens operacionais.');
      return;
    }

    const alreadySent = apt.appointment_rescheduled_whatsapp_sent === true;

    const confirmMessage = alreadySent
      ? `Já existe registro de WhatsApp de remarcação enviado para ${apt.patient_name}.\n\nDeseja reenviar mesmo assim?`
      : `Enviar WhatsApp de remarcação para ${apt.patient_name}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/appointments/${apt.id}/send-rescheduled-whatsapp`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast?.success(response.data?.message || 'WhatsApp de remarcação enviado com sucesso!');

      setSelectedEvent({
        ...selectedEvent,
        resource: {
          ...apt,
          appointment_rescheduled_whatsapp_sent: true,
          appointment_rescheduled_whatsapp_sent_at: new Date().toISOString(),
        }
      });

      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao enviar WhatsApp de remarcação:', error);
      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar WhatsApp de remarcação.';
      toast?.error(msg);
      window.alert(msg);
    }
  };

  const handleSendCancelledWhatsApp = async () => {
    if (!selectedEvent?.resource?.id) return;

    const apt = selectedEvent.resource;

    if (!canUseOperationalActions(apt)) {
      toast?.error('Este agendamento está encerrado e não permite mensagens operacionais.');
      return;
    }

    const alreadySent = apt.appointment_cancelled_whatsapp_sent === true;

    const confirmMessage = alreadySent
      ? `Já existe registro de WhatsApp de cancelamento enviado para ${apt.patient_name}.\n\nDeseja reenviar mesmo assim?`
      : `Enviar WhatsApp de cancelamento para ${apt.patient_name}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/appointments/${apt.id}/send-cancelled-whatsapp`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast?.success(response.data?.message || 'WhatsApp de cancelamento enviado com sucesso!');

      setSelectedEvent({
        ...selectedEvent,
        resource: {
          ...apt,
          appointment_cancelled_whatsapp_sent: true,
          appointment_cancelled_whatsapp_sent_at: new Date().toISOString(),
        }
      });

      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao enviar WhatsApp de cancelamento:', error);
      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar WhatsApp de cancelamento.';
      toast?.error(msg);
      window.alert(msg);
    }
  };
  
  const handleCheckIn = () => {
    if (!can('appointments.update') || !can('clinical_records.view')) {
      toast?.error('Você não tem permissão para iniciar atendimentos.');
      return;
    }
    if (!selectedEvent) return;
    const apt = selectedEvent.resource;
    if (!canStartAppointment(apt)) {
      toast?.error('Este agendamento já foi encerrado ou não permite check-in.');
      return;
    }
    if (agendaDateStorageKey) {
      localStorage.setItem(
        agendaDateStorageKey,
        moment(currentDate).format('YYYY-MM-DD')
      );
    }
    localStorage.setItem(
      'agendaReturnContext',
      JSON.stringify({
        view: currentView,
        date: moment(currentDate).format('YYYY-MM-DD'),
      })
    );
    // Guarda o agendamento ativo para o Prontuário
    localStorage.setItem('activeAppointment', JSON.stringify({
      id: apt.id,
      patient_id: apt.patient_id,
      appointment_date: apt.appointment_date,
      start_time: apt.start_time,
      end_time: apt.end_time || '',
      procedure: apt.procedure || '',
      treatment_id: apt.treatment_id || '',
      aligner_treatment_id: apt.aligner_treatment_id || '',
      professional_id: apt.professional_id || '',
      professional_name: apt.professional_name || '',
      origin: 'agenda'
    }));
    // Navega para o prontuário desse paciente
    window.location.href = `/prontuarios?patientId=${encodeURIComponent(apt.patient_id)}&activeAppt=${encodeURIComponent(apt.id)}&from=agenda`;
  };

  const handleOpenPatientRecord = () => {
    const patientId = selectedEvent?.resource?.patient_id;
    if (!patientId || !can('clinical_records.view')) return;

    window.location.href = `/prontuarios?patientId=${encodeURIComponent(patientId)}&tab=overview`;
  };

  const isGuestAppointment = (apt) => {
    return !apt?.patient_id;
  };

  const handleCreatePatientFromGuestAppointment = async () => {
    if (!selectedEvent) return;

    const apt = selectedEvent.resource;

    if (!isGuestAppointment(apt)) {
      handleCheckIn();
      return;
    }

    if (!apt.patient_name) {
      toast?.error('Este agendamento avulso não possui nome do paciente.');
      return;
    }

    const confirmed = window.confirm(
      `Criar cadastro do paciente "${apt.patient_name}" a partir deste agendamento?`
    );

    if (!confirmed) return;

    try {
      const authHeaders = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      };

      const patientPayload = {
        full_name: apt.patient_name,
        birth_date: '',
        phone: apt.patient_phone || '',
        administrative_notes: `Paciente criado automaticamente a partir do agendamento avulso de ${moment(apt.appointment_date).format('DD/MM/YYYY')} às ${apt.start_time}.`
      };

      const patientRes = await axios.post(`${API_URL}/patients`, patientPayload, authHeaders);
      const newPatient = patientRes.data;

      const appointmentPayload = {
        patient_id: newPatient.id,
        patient_name: apt.patient_name,
        patient_phone: apt.patient_phone || '',
        appointment_date: apt.appointment_date,
        start_time: apt.start_time,
        end_time: apt.end_time || undefined,
        procedure: apt.procedure,
        status: apt.status || 'Agendado',
        notes: apt.notes || '',
        professional_id: apt.professional_id || undefined,
        professional_name: apt.professional_name || undefined,
        booking_source: apt.booking_source || 'manual',
        online_booking_status: apt.online_booking_status || undefined,
        treatment_id: apt.treatment_id || undefined,
        reminders: apt.reminders || []
      };

      const appointmentRes = await axios.put(
        `${API_URL}/appointments/${apt.id}`,
        appointmentPayload,
        authHeaders
      );

      const updatedAppointment = appointmentRes.data;

      setPatients((prev) => [...prev, newPatient]);
      setAppointments((prev) =>
        prev.map((item) => item.id === updatedAppointment.id ? updatedAppointment : item)
      );

      localStorage.setItem('activeAppointment', JSON.stringify({
        id: updatedAppointment.id,
        patient_id: newPatient.id,
        appointment_date: updatedAppointment.appointment_date,
        start_time: updatedAppointment.start_time,
        end_time: updatedAppointment.end_time || '',
        procedure: updatedAppointment.procedure || ''
      }));

      toast?.success('Paciente criado e vinculado ao agendamento.');

      window.dispatchEvent(new CustomEvent('appointments:changed'));
      window.location.href = `/prontuarios?patientId=${encodeURIComponent(newPatient.id)}&activeAppt=${encodeURIComponent(updatedAppointment.id)}`;
    } catch (error) {
      console.error('Erro ao criar paciente a partir do agendamento avulso:', error);
      toast?.error(
        error.response?.data?.detail ||
        'Erro ao criar paciente a partir do agendamento avulso.'
      );
    }
  };

  const handleGenerateAppointmentInvoice = async () => {
    const apt = selectedEvent?.resource;
    if (!apt?.id || generatingInvoice) return;

    const amount = Number(apt.amount ?? apt.expected_amount ?? apt.produced_value ?? 0);

    if (apt.status !== 'Realizado') {
      toast?.error('A fatura só pode ser gerada após o atendimento ser marcado como Realizado.');
      return;
    }

    if (apt.push_to_finance !== true) {
      toast?.error('Este agendamento não está incluído na previsão financeira.');
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast?.error('Informe um valor maior que zero antes de gerar a fatura.');
      return;
    }

    if (apt.invoice_id) {
      toast?.info?.('Este agendamento já possui uma fatura vinculada.');
      return;
    }

    if (!window.confirm(`Gerar fatura de R$ ${amount.toFixed(2).replace('.', ',')} para ${apt.patient_name}?`)) {
      return;
    }

    try {
      setGeneratingInvoice(true);

      const response = await axios.post(
        `${API_URL}/billing/appointments/${apt.id}/invoice`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      const invoice = response.data || {};
      const updatedAppointment = {
        ...apt,
        invoice_id: invoice.id,
        invoice_number: invoice.invoice_number,
        invoiced_value: Number(invoice.total_amount ?? amount),
        received_value: Number(invoice.received_amount ?? 0),
        financial_stage: 'invoiced',
      };

      setSelectedEvent((previous) => ({
        ...previous,
        resource: updatedAppointment,
      }));

      setAppointments((previous) =>
        previous.map((item) =>
          item.id === apt.id ? updatedAppointment : item
        )
      );

      toast?.success(
        invoice.invoice_number
          ? `Fatura nº ${invoice.invoice_number} gerada com sucesso!`
          : 'Fatura gerada com sucesso!'
      );

      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao gerar fatura do agendamento:', error);
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao gerar fatura do agendamento.';
      toast?.error(message);
      window.alert(message);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const openPaymentModal = async () => {
    const apt = selectedEvent?.resource;
    if (!apt?.invoice_id) {
      toast?.error('Este agendamento ainda não possui fatura vinculada.');
      return;
    }

    const invoicedValue = Number(apt.invoiced_value ?? apt.amount ?? apt.expected_amount ?? 0);
    const receivedValue = Number(apt.received_value ?? 0);
    const remainingValue = Math.max(invoicedValue - receivedValue, 0);

    if (remainingValue <= 0) {
      toast?.info?.('Esta fatura já está totalmente recebida.');
      return;
    }

    let methods = paymentMethods;
    if (!methods.length) {
      try {
        const response = await axios.get(`${API_URL}/billing/payment-methods`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        methods = Array.isArray(response.data) ? response.data : [];
        setPaymentMethods(methods);
      } catch (error) {
        console.error('Erro ao carregar formas de pagamento:', error);
        methods = [
          { id: 'pix', name: 'PIX' },
          { id: 'dinheiro', name: 'Dinheiro' },
          { id: 'cartao_credito', name: 'Cartão de Crédito' },
          { id: 'cartao_debito', name: 'Cartão de Débito' },
          { id: 'transferencia', name: 'Transferência Bancária' },
        ];
        setPaymentMethods(methods);
      }
    }

    const defaultMethod = methods.find((item) => item.id === 'pix') || methods[0] || { id: 'pix', name: 'PIX' };

    setPaymentForm({
      amount: remainingValue.toFixed(2),
      payment_method_id: defaultMethod.id,
      payment_method_name: defaultMethod.name,
      payment_date: moment().format('YYYY-MM-DD'),
      notes: '',
    });
    setShowPaymentModal(true);
  };

  const handleRegisterPayment = async (event) => {
    event.preventDefault();

    const apt = selectedEvent?.resource;
    if (!apt?.invoice_id || registeringPayment) return;

    const amount = Number(String(paymentForm.amount).replace(',', '.'));
    const invoicedValue = Number(apt.invoiced_value ?? apt.amount ?? apt.expected_amount ?? 0);
    const currentReceived = Number(apt.received_value ?? 0);
    const remainingValue = Math.max(invoicedValue - currentReceived, 0);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast?.error('Informe um valor recebido maior que zero.');
      return;
    }

    if (amount > remainingValue + 0.005) {
      toast?.error(`O valor não pode ultrapassar o saldo de ${formatCurrencyBR(remainingValue)}.`);
      return;
    }

    const selectedMethod = paymentMethods.find(
      (item) => item.id === paymentForm.payment_method_id
    );

    try {
      setRegisteringPayment(true);

      await axios.post(
        `${API_URL}/billing/payments`,
        {
          invoice_id: apt.invoice_id,
          appointment_id: apt.id,
          amount,
          payment_method_id: paymentForm.payment_method_id,
          payment_method_name: selectedMethod?.name || paymentForm.payment_method_name || 'Pagamento',
          payment_date: moment(paymentForm.payment_date, 'YYYY-MM-DD').startOf('day').toISOString(),
          notes: paymentForm.notes?.trim() || undefined,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      const newReceivedValue = Math.min(currentReceived + amount, invoicedValue);
      const isFullyReceived = newReceivedValue >= invoicedValue - 0.005;
      const updatedAppointment = {
        ...apt,
        received_value: newReceivedValue,
        financial_stage: isFullyReceived ? 'received' : 'partially_received',
      };

      setSelectedEvent((previous) => ({
        ...previous,
        resource: updatedAppointment,
      }));

      setAppointments((previous) =>
        previous.map((item) => item.id === apt.id ? updatedAppointment : item)
      );

      setShowPaymentModal(false);
      toast?.success(
        isFullyReceived
          ? 'Pagamento registrado. Fatura totalmente recebida!'
          : 'Pagamento parcial registrado com sucesso!'
      );
      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao registrar pagamento.';
      toast?.error(message);
      window.alert(message);
    } finally {
      setRegisteringPayment(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedEvent) return;
    if (
      selectedEvent.resource.status === 'Realizado' &&
      newStatus !== 'Realizado' &&
      !window.confirm(
        'Este atendimento já foi marcado como realizado e pode possuir evolução clínica.\n\n' +
        'Deseja realmente reabrir o agendamento? A evolução existente será preservada.'
      )
    ) {
      return;
    }
    if (
      newStatus === 'Realizado' &&
      selectedEvent.resource.status !== 'Realizado' &&
      !selectedEvent.resource.invoice_id
    ) {
      const apt = selectedEvent.resource;
      let methods = paymentMethods;
      if (!methods.length) {
        try {
          const response = await axios.get(`${API_URL}/billing/payment-methods`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          methods = Array.isArray(response.data) ? response.data : [];
          setPaymentMethods(methods);
        } catch (error) {
          console.error('Erro ao carregar formas de pagamento:', error);
        }
      }
      const defaultMethod = methods.find((item) => item.id === 'pix') || methods[0] || { id: 'pix', name: 'PIX' };
      const suggestedAmount = Number(apt.amount ?? apt.expected_amount ?? 0);
      setFinancialCompletionForm({
        amount: suggestedAmount > 0 ? suggestedAmount.toFixed(2) : '',
        discount_amount: '',
        receive_now: true,
        payment_method_id: defaultMethod.id,
        payment_method_name: defaultMethod.name,
        payment_date: moment().format('YYYY-MM-DD'),
        due_date: moment().format('YYYY-MM-DD'),
        payment_type: 'single',
        installment_count: 2,
        notes: '',
      });
      setShowFinancialCompletionModal(true);
      return;
    }
    try {
      const apt = selectedEvent.resource;
      const payload = {
        patient_id: apt.patient_id,
        patient_name: apt.patient_name,
        patient_phone: apt.patient_phone,
        appointment_date: apt.appointment_date,
        start_time: apt.start_time,
        end_time: apt.end_time || undefined,
        procedure: apt.procedure,
        status: newStatus,
        notes: apt.notes || '',
        professional_id: apt.professional_id || undefined,
        professional_name: apt.professional_name || undefined,
        booking_source: apt.booking_source || 'manual',
        online_booking_status: apt.online_booking_status || undefined,
        treatment_id: apt.treatment_id || undefined,
        reminders: apt.reminders || []
      };

      await axios.put(`${API_URL}/appointments/${apt.id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast?.success(newStatus === 'Cancelado' ? 'Agendamento cancelado.' : 'Status atualizado!');

      setSelectedEvent({
        ...selectedEvent,
        resource: { ...apt, status: newStatus }
      });

      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast?.error(
        error?.response?.status === 403
          ? 'Você não tem permissão para realizar esta alteração.'
          : (error?.response?.data?.detail || 'Erro ao atualizar status')
      );
    }
  };

  const handleCompleteFinancial = async (event, skipFinancial = false) => {
    event?.preventDefault?.();
    const apt = selectedEvent?.resource;
    if (!apt?.id || completingFinancial) return;

    const amount = Number(String(financialCompletionForm.amount || '').replace(',', '.'));
    const discountAmount = Number(String(financialCompletionForm.discount_amount || '').replace(',', '.')) || 0;
    if (!skipFinancial && (!Number.isFinite(amount) || amount <= 0)) {
      toast?.error('Informe um valor maior que zero ou finalize sem lançamento financeiro.');
      return;
    }
    if (!skipFinancial && (discountAmount < 0 || discountAmount >= amount)) {
      toast?.error('O desconto deve ser menor que o valor do atendimento.');
      return;
    }

    const selectedMethod = paymentMethods.find(
      (item) => item.id === financialCompletionForm.payment_method_id
    );

    try {
      setCompletingFinancial(true);
      const response = await axios.post(
        `${API_URL}/billing/appointments/${apt.id}/complete-financial`,
        {
          amount: skipFinancial ? 0 : amount,
          discount_amount: skipFinancial ? 0 : discountAmount,
          receive_now: skipFinancial ? false : financialCompletionForm.receive_now,
          skip_financial: skipFinancial,
          due_date: skipFinancial ? null : moment(financialCompletionForm.due_date, 'YYYY-MM-DD').startOf('day').toISOString(),
          payment_type: financialCompletionForm.receive_now ? 'single' : financialCompletionForm.payment_type,
          installment_count: financialCompletionForm.receive_now
            ? 1
            : Number(financialCompletionForm.installment_count || 2),
          payment_method_id: financialCompletionForm.receive_now && !skipFinancial
            ? financialCompletionForm.payment_method_id
            : null,
          payment_method_name: financialCompletionForm.receive_now && !skipFinancial
            ? (selectedMethod?.name || financialCompletionForm.payment_method_name)
            : null,
          payment_date: financialCompletionForm.receive_now && !skipFinancial
            ? moment(financialCompletionForm.payment_date, 'YYYY-MM-DD').startOf('day').toISOString()
            : null,
          notes: financialCompletionForm.notes?.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      const updatedAppointment = response.data?.appointment || { ...apt, status: 'Realizado' };
      const invoice = response.data?.invoice;
      const normalizedAppointment = {
        ...updatedAppointment,
        invoice_number: invoice?.invoice_number || updatedAppointment.invoice_number,
      };
      setSelectedEvent((previous) => ({ ...previous, resource: normalizedAppointment }));
      setAppointments((previous) => previous.map(
        (item) => item.id === apt.id ? normalizedAppointment : item
      ));
      setShowFinancialCompletionModal(false);
      toast?.success(
        skipFinancial
          ? 'Atendimento finalizado sem lançamento financeiro.'
          : financialCompletionForm.receive_now
            ? 'Atendimento finalizado e pagamento registrado!'
            : 'Atendimento finalizado e conta a receber criada!'
      );
      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (error) {
      console.error('Erro ao finalizar atendimento financeiramente:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao finalizar atendimento.');
    } finally {
      setCompletingFinancial(false);
    }
  };

  const handleCancelAppointment = async () => {
    const appointment = selectedEvent?.resource;
    if (!appointment || appointment.status === 'Cancelado') return;
    if (!window.confirm(`Cancelar o agendamento de ${appointment.patient_name}? O registro permanecerá no histórico.`)) return;
    await handleStatusChange('Cancelado');
  };

  const getActiveProfessionals = () => {
    const list = clinicSettings?.professionals || [];
    const active = list.filter((p) => p?.id && p?.name && p?.is_active !== false);

    if (active.length > 0) return active;

    if (clinicSettings?.professional_name) {
      return [{
        id: 'main',
        name: clinicSettings.professional_name,
        is_main: true,
      }];
    }

    return [];
  };

  const getSingleActiveProfessional = () => {
    const activeProfessionals = getWritableProfessionals();
    return activeProfessionals.length === 1 ? activeProfessionals[0] : null;
  };

  const getWritableProfessionals = () => {
    const activeProfessionals = getActiveProfessionals();
    if (!['busy_only', 'own'].includes(user?.agenda_scope)) return activeProfessionals;
    return activeProfessionals.filter((professional) => String(professional.id) === String(user?.professional_id));
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();

    try {
      const startDate = moment(blockForm.appointment_date, 'YYYY-MM-DD');
      const endDate = moment(
        blockForm.block_mode === 'period'
          ? blockForm.end_date || blockForm.appointment_date
          : blockForm.appointment_date,
        'YYYY-MM-DD'
      );

      if (!startDate.isValid() || !endDate.isValid()) {
        toast?.error('Informe uma data válida.');
        return;
      }

      if (endDate.isBefore(startDate, 'day')) {
        toast?.error('A data final não pode ser anterior à data inicial.');
        return;
      }

      const requests = [];
      let cursor = startDate.clone();

      while (cursor.isSameOrBefore(endDate, 'day')) {
        const isFullDay = blockForm.block_mode === 'full_day' || blockForm.block_mode === 'period';

        requests.push(
          axios.post(`${API_URL}/appointment-blocks`, {
            appointment_date: cursor.format('YYYY-MM-DD'),
            start_time: isFullDay ? '00:00' : blockForm.start_time,
            end_time: isFullDay ? '23:59' : blockForm.end_time,
            professional_id: blockForm.professional_id || undefined,
            professional_name: blockForm.professional_name || undefined,
            reason: blockForm.reason || (
              blockForm.block_mode === 'period'
                ? 'Período bloqueado'
                : blockForm.block_mode === 'full_day'
                  ? 'Dia bloqueado'
                  : 'Horário bloqueado'
            ),
            block_type: blockForm.block_mode,
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
        );

        cursor.add(1, 'day');
      }

      await Promise.all(requests);

      toast?.success(
        blockForm.block_mode === 'period'
          ? 'Período bloqueado com sucesso!'
          : blockForm.block_mode === 'full_day'
            ? 'Dia bloqueado com sucesso!'
            : 'Bloqueio criado com sucesso!'
      );

      setShowBlockForm(false);
      window.dispatchEvent(new CustomEvent('appointment-blocks:changed'));
    } catch (error) {
      console.error('Erro ao criar bloqueio:', error);
      toast?.error('Erro ao criar bloqueio de agenda');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Tem certeza que deseja remover este bloqueio?')) return;

    try {
      await axios.delete(`${API_URL}/appointment-blocks/${blockId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast?.success('Bloqueio removido com sucesso!');
      setShowEventModal(false);
      window.dispatchEvent(new CustomEvent('appointment-blocks:changed'));
    } catch (error) {
      console.error('Erro ao remover bloqueio:', error);
      toast?.error('Erro ao remover bloqueio');
    }
  };

  const isMobileLike =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    window.matchMedia('(max-width: 768px)').matches;

  const openWhatsAppWeb = async () => {
    const patientId = selectedPatientContext?.id || '';
    const patientName = selectedPatientContext?.full_name || '';

    if (isMobileLike) {
      toast?.info?.('No celular, use esse atalho quando houver um paciente com telefone disponível.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      params.set('source', 'agenda');
      if (patientId) params.set('patient_id', patientId);
      if (patientName) params.set('patient_name', patientName);

      const response = await fetch(`${API_URL}/extension/session-ticket?${params.toString()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data?.ticket) {
        console.error('Erro ao gerar ticket da extensão:', data);
        return;
      }

      const url = new URL('https://web.whatsapp.com');
      url.searchParams.set('odontoassist_source', 'agenda');
      if (patientId) url.searchParams.set('patientId', patientId);
      if (patientName) url.searchParams.set('patientName', patientName);
      url.searchParams.set('ext_ticket', data.ticket);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erro ao abrir integração com WhatsApp:', err);
    }
  };

  const handleSendOnlineBookingLinkWhatsAppFromAgenda = async () => {
    try {
      setSendingOnlineBookingWhatsApp(true);

      const payload =
        onlineBookingWhatsappMode === 'registered'
          ? { patient_id: onlineBookingPatientId }
          : {
              patient_name: onlineBookingGuestName,
              patient_phone: onlineBookingGuestPhone,
            };

      if (onlineBookingWhatsappMode === 'registered' && !onlineBookingPatientId) {
        toast?.error('Selecione um paciente cadastrado.');
        return;
      }

      if (onlineBookingWhatsappMode === 'guest') {
        if (!onlineBookingGuestName.trim()) {
          toast?.error('Informe o nome do paciente.');
          return;
        }

        if (!onlineBookingGuestPhone.trim()) {
          toast?.error('Informe o WhatsApp com DDD.');
          return;
        }
      }

      const response = await axios.post(
        `${API_URL}/appointments/send-online-booking-link-whatsapp`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );

      toast?.success(response.data?.message || 'Link enviado por WhatsApp com sucesso.');
      setShowOnlineBookingWhatsappModal(false);
      setOnlineBookingPatientId('');
      setOnlineBookingGuestName('');
      setOnlineBookingGuestPhone('');
      setOnlineBookingWhatsappMode('registered');
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar link de autoagendamento por WhatsApp.';

      toast?.error(msg);
      window.alert(msg);
    } finally {
      setSendingOnlineBookingWhatsApp(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Agenda Inteligente</h2>
              <p className="text-gray-600">Organize consultas e horários da clínica.</p>
            </div>
            <div className="flex flex-wrap gap-2 w-full lg:w-auto justify-start lg:justify-end">
                {can('appointments.create') && <button
                  onClick={() => {
                    const singleProfessional = getSingleActiveProfessional();

                    setEditing(null);
                    setSelectedPatientContext(null);
                    setPresetDateTime({ date: '', time: '' });

                    if (singleProfessional) {
                      setEditing({
                        professional_id: singleProfessional.id,
                        professional_name: singleProfessional.name,
                      });
                    }

                    setShowForm(true);
                  }}
                  data-onboarding-id="new-appointment"
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Agendamento
                </button>}

                <button
                  type="button"
                  onClick={() => setShowAdditionalAgendaActions((current) => !current)}
                  aria-expanded={showAdditionalAgendaActions}
                  className="flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Outras ações
                  <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${showAdditionalAgendaActions ? 'rotate-180' : ''}`} />
                </button>
            </div>
          </div>

          {showAdditionalAgendaActions && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => {
                  refreshAgenda();
                  setShowAdditionalAgendaActions(false);
                }}
                className="flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Atualizar agenda
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowAdditionalAgendaActions(false);
                  openWhatsAppWeb();
                }}
                className="flex items-center rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 hover:bg-green-100"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Abrir WhatsApp Web
              </button>

                {can('appointments.create') && <button
                  onClick={() => {
                    const professionals = getWritableProfessionals();
                    const mainProf = professionals.find((p) => p.is_main) || professionals[0] || null;

                    setBlockForm({
                      block_mode: 'time',
                      appointment_date: moment(currentDate).format('YYYY-MM-DD'),
                      end_date: moment(currentDate).format('YYYY-MM-DD'),
                      start_time: '08:00',
                      end_time: '09:00',
                      professional_id: mainProf?.id || '',
                      professional_name: mainProf?.name || '',
                      reason: '',
                    });

                    setShowBlockForm(true);
                    setShowAdditionalAgendaActions(false);
                  }}
                  className="flex items-center rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Bloquear horário
                </button>}

              {(clinicSettings?.online_booking_enabled && clinicSettings?.online_booking_token) && (
                <>
                  <button
                    onClick={() => {
                      const link = `${window.location.origin}/public/autoagendamento/${clinicSettings.online_booking_token}`;
                      navigator.clipboard.writeText(link);
                      toast?.success?.('Link de autoagendamento copiado!');
                      window.alert('Link de autoagendamento copiado.');
                      setShowAdditionalAgendaActions(false);
                    }}
                    className="flex items-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Copiar link de autoagendamento
                  </button>

                  {canSendOnlineBookingWhatsapp && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowOnlineBookingWhatsappModal(true);
                        setShowAdditionalAgendaActions(false);
                      }}
                      disabled={sendingOnlineBookingWhatsApp}
                      className="flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      title="Enviar link de autoagendamento por WhatsApp"
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      {sendingOnlineBookingWhatsApp
                        ? 'Enviando...'
                        : 'Enviar autoagendamento por WhatsApp'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Cards rápidos */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Hoje</p>
                <p className="text-2xl font-bold text-blue-600">
                  {appointments.filter(a => a.appointment_date === moment().format('YYYY-MM-DD')).length}
                </p>
              </div>
              <CalendarIcon className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Esta Semana</p>
                <p className="text-2xl font-bold text-green-600">
                  {appointments.filter(a => moment(a.appointment_date).isSame(moment(), 'week')).length}
                </p>
              </div>
              <CalendarDays className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Confirmados</p>
                <p className="text-2xl font-bold text-purple-600">
                  {appointments.filter(a => a.status === 'Confirmado').length}
                </p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {appointments.filter(a => a.status === 'Agendado').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filtrar por profissional
              </label>
              <select
                value={professionalFilter}
                onChange={(e) => setProfessionalFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Todos os profissionais</option>
                {getActiveProfessionals().map((prof) => (
                  <option key={prof.id} value={prof.id}>
                    {prof.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filtrar por origem
              </label>
              <select
                value={originFilter}
                onChange={(e) => setOriginFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Todos</option>
                <option value="manual">Agendamentos manuais</option>
                <option value="online">Agendamentos online</option>
                <option value="commercial">Atividades comerciais</option>
                <option value="blocks">Somente bloqueios</option>
              </select>
            </div>
          </div>          
          {/* Legenda por status */}
          <div className="flex flex-wrap items-center gap-3 mb-3">
            {STATUS.map(s => (
              <div key={s.value} className="flex items-center gap-2 text-sm">
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="text-gray-700">{s.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#8b5cf6' }} />
              <span className="text-gray-700">Online</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#7c3aed' }} />
              <span className="text-gray-700">Atividade comercial</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#6b7280' }} />
              <span className="text-gray-700">Bloqueado</span>
            </div>            
          </div>

          <div style={{ height: 600 }}>
            <Calendar
              localizer={localizer}
              events={allCalendarEvents}
              min={calendarTimeRange.min}
              max={calendarTimeRange.max}
              scrollToTime={calendarTimeRange.min}              
              startAccessor="start"
              endAccessor="end"
              onSelectEvent={handleSelectEvent}
              onSelectSlot={can('appointments.create') ? handleSelectSlot : undefined}
              selectable={can('appointments.create')}
              popup
              views={['month', 'week', 'day', 'agenda']}
              view={currentView}
              onView={handleCalendarViewChange}
              date={currentDate}
              onNavigate={handleCalendarNavigate}
              messages={messages}
              components={{ event: CalendarEvent }}
              eventPropGetter={(event) => ({ style: event.style })}
            />
          </div>
        </div>

        {showOnlineBookingWhatsappModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowOnlineBookingWhatsappModal(false);
            }}
          >
            <div className="bg-white rounded-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Enviar autoagendamento por WhatsApp
                </h3>
                <button
                  onClick={() => setShowOnlineBookingWhatsappModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={onlineBookingWhatsappMode === 'registered'}
                      onChange={() => setOnlineBookingWhatsappMode('registered')}
                    />
                    Paciente cadastrado
                  </label>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={onlineBookingWhatsappMode === 'guest'}
                      onChange={() => setOnlineBookingWhatsappMode('guest')}
                    />
                    Paciente avulso
                  </label>
                </div>

                {onlineBookingWhatsappMode === 'registered' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Paciente
                    </label>
                    <select
                      value={onlineBookingPatientId}
                      onChange={(e) => setOnlineBookingPatientId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Selecione o paciente</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.full_name || patient.name}
                          {patient.phone ? ` — ${patient.phone}` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      O sistema usará o telefone/WhatsApp cadastrado no paciente.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do paciente
                      </label>
                      <input
                        type="text"
                        value={onlineBookingGuestName}
                        onChange={(e) => setOnlineBookingGuestName(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="Ex.: Maria Silva"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        WhatsApp com DDD
                      </label>
                      <input
                        type="text"
                        value={onlineBookingGuestPhone}
                        onChange={(e) => setOnlineBookingGuestPhone(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="Ex.: 81999999999"
                      />
                    </div>
                  </>
                )}

                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                  Será enviado o link de autoagendamento configurado em Configurações.
                </div>
              </div>

              <div className="flex justify-end gap-2 p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowOnlineBookingWhatsappModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={sendingOnlineBookingWhatsApp}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={handleSendOnlineBookingLinkWhatsAppFromAgenda}
                  disabled={sendingOnlineBookingWhatsApp}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {sendingOnlineBookingWhatsApp ? 'Enviando...' : 'Enviar link'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de detalhes do evento ou bloqueio */}
        {showEventModal && selectedEvent && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowEventModal(false); }}
          >
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedEvent.resource?.is_block ? 'Detalhes do Bloqueio' : selectedEvent.resource?.is_partner_activity ? 'Atividade Comercial' : 'Detalhes do Agendamento'}
                </h3>
                <button onClick={() => setShowEventModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-3 overflow-y-auto min-h-0">
                {selectedEvent.resource?.is_partner_activity ? (
                  <>
                    <div><span className="font-medium">Parceiro: </span>{selectedEvent.resource.partner_name || 'Parceiro'}</div>
                    <div><span className="font-medium">Atividade: </span>{selectedEvent.resource.title}</div>
                    <div><span className="font-medium">Data: </span>{moment(selectedEvent.start).format('DD/MM/YYYY')}</div>
                    <div><span className="font-medium">Horário: </span>{moment(selectedEvent.start).format('HH:mm')} - {moment(selectedEvent.end).format('HH:mm')}</div>
                    <div><span className="font-medium">Situação: </span>{selectedEvent.resource.status}</div>
                    <div><span className="font-medium">Prioridade: </span>{selectedEvent.resource.priority}</div>
                    {selectedEvent.resource.description && <div><span className="font-medium">Descrição: </span>{selectedEvent.resource.description}</div>}
                    {selectedEvent.resource.location && <div><span className="font-medium">Local: </span>{selectedEvent.resource.location}</div>}
                    {selectedEvent.resource.meeting_url && <div><a href={selectedEvent.resource.meeting_url} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline">Abrir link da reunião</a></div>}
                    <div className="flex justify-end gap-2 pt-4 border-t"><button type="button" onClick={() => setShowEventModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Fechar</button></div>
                  </>
                ) : selectedEvent.resource?.is_block ? (
                  <>
                    <div>
                      <span className="font-medium">Tipo: </span>
                      Bloqueio de agenda
                    </div>

                    <div>
                      <span className="font-medium">Data: </span>
                      {moment(selectedEvent.start).format('DD/MM/YYYY')}
                    </div>

                    <div>
                      <span className="font-medium">Horário: </span>
                      {moment(selectedEvent.start).format('HH:mm')} - {moment(selectedEvent.end).format('HH:mm')}
                    </div>

                    {selectedEvent.resource.professional_name && (
                      <div>
                        <span className="font-medium">Profissional: </span>
                        {selectedEvent.resource.professional_name}
                      </div>
                    )}

                    {selectedEvent.resource.reason && (
                      <div>
                        <span className="font-medium">Motivo: </span>
                        {selectedEvent.resource.reason}
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => setShowEventModal(false)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Fechar
                      </button>

                      {can('appointments.update') && <button
                        type="button"
                        onClick={() => handleDeleteBlock(selectedEvent.resource.id)}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                      >
                        Remover bloqueio
                      </button>}
                    </div>
                  </>
                ) : (
                  <>
                    {selectedEvent.resource.conflict_count > 0 && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <TriangleAlert className="mt-0.5 h-4 w-4 flex-none" />
                        <span>
                          <strong>Conflito de horário:</strong>{' '}
                          este atendimento está sobreposto a {selectedEvent.resource.conflict_count} outro(s) agendamento(s).
                        </span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Users className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="font-medium">{selectedEvent.resource.patient_name}</span>
                    </div>

                    {!selectedEvent.resource.patient_id && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        <span className="font-semibold">Paciente avulso:</span>{' '}
                        este agendamento ainda não está vinculado a um prontuário.
                      </div>
                    )}

                    <div className="flex items-center">
                      <CalendarIcon className="w-5 h-5 text-gray-500 mr-2" />
                      <span>{moment(selectedEvent.start).format('DD/MM/YYYY')}</span>
                    </div>

                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-500 mr-2" />
                      <span>{moment(selectedEvent.start).format('HH:mm')} - {moment(selectedEvent.end).format('HH:mm')}</span>
                    </div>

                    <div>
                      <span className="font-medium">Procedimento: </span>
                      {selectedEvent.resource.procedure}
                    </div>

                    {selectedEvent.resource.professional_name && (
                      <div>
                        <span className="font-medium">Profissional: </span>
                        {selectedEvent.resource.professional_name}
                      </div>
                    )}

                    {selectedEvent.resource.booking_source === 'online' && (
                      <div>
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                          Agendamento online pelo paciente
                        </span>
                      </div>
                    )}

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2 text-sm">
                      <p className="font-semibold text-gray-800">
                        Histórico de notificações
                      </p>

                      <div className="flex flex-col gap-1">
                        {selectedEvent.resource.appointment_confirmation_email_sent ? (
                          <span className="text-green-700">
                            E-mail de confirmação enviado
                            {selectedEvent.resource.appointment_confirmation_email_sent_at
                              ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_confirmation_email_sent_at)}`
                              : ''}
                          </span>
                        ) : (
                          <span className="text-gray-500">
                            E-mail de confirmação ainda não enviado
                          </span>
                        )}

                        {hasWhatsAppModule ? (
                          <>

                        {selectedEvent.resource.appointment_confirmation_whatsapp_sent ? (
                          <div className="space-y-1">
                            <span className="block text-green-700">
                              WhatsApp de confirmação enviado
                              {selectedEvent.resource.appointment_confirmation_whatsapp_sent_at
                                ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_confirmation_whatsapp_sent_at)}`
                                : ''}
                            </span>

                            {selectedEvent.resource.appointment_confirmation_whatsapp_delivered && (
                              <span className="block text-green-700">
                                WhatsApp entregue
                                {selectedEvent.resource.appointment_confirmation_whatsapp_delivered_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_confirmation_whatsapp_delivered_at)}`
                                  : ''}
                              </span>
                            )}

                            {selectedEvent.resource.appointment_confirmation_whatsapp_read && (
                              <span className="block text-green-700">
                                WhatsApp lido
                                {selectedEvent.resource.appointment_confirmation_whatsapp_read_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_confirmation_whatsapp_read_at)}`
                                  : ''}
                              </span>
                            )}

                            {selectedEvent.resource.appointment_confirmation_whatsapp_failed && (
                              <span className="block text-red-700">
                                Falha no envio do WhatsApp
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500">
                            WhatsApp de confirmação ainda não enviado
                          </span>
                        )}

                        {selectedEvent.resource.appointment_reminder_whatsapp_sent ? (
                          <div className="space-y-1">
                            <span className="block text-green-700">
                              WhatsApp de lembrete enviado
                              {selectedEvent.resource.appointment_reminder_whatsapp_sent_at
                                ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_reminder_whatsapp_sent_at)}`
                                : ''}
                            </span>

                            {selectedEvent.resource.appointment_reminder_whatsapp_read && (
                              <span className="block text-green-700">
                                Lembrete lido
                                {selectedEvent.resource.appointment_reminder_whatsapp_read_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_reminder_whatsapp_read_at)}`
                                  : ''}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500">
                            WhatsApp de lembrete ainda não enviado
                          </span>
                        )}

                        {selectedEvent.resource.appointment_rescheduled_whatsapp_sent && (
                          <div className="space-y-1">
                            <span className="block text-yellow-700">
                              WhatsApp de remarcação enviado
                              {selectedEvent.resource.appointment_rescheduled_whatsapp_sent_at
                                ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_rescheduled_whatsapp_sent_at)}`
                                : ''}
                            </span>

                            {selectedEvent.resource.appointment_rescheduled_whatsapp_delivered && (
                              <span className="block text-yellow-700">
                                Remarcação entregue
                                {selectedEvent.resource.appointment_rescheduled_whatsapp_delivered_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_rescheduled_whatsapp_delivered_at)}`
                                  : ''}
                              </span>
                            )}

                            {selectedEvent.resource.appointment_rescheduled_whatsapp_read && (
                              <span className="block text-yellow-700">
                                Remarcação lida
                                {selectedEvent.resource.appointment_rescheduled_whatsapp_read_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_rescheduled_whatsapp_read_at)}`
                                  : ''}
                              </span>
                            )}

                            {selectedEvent.resource.appointment_rescheduled_whatsapp_failed && (
                              <span className="block text-red-700">
                                Falha no WhatsApp de remarcação
                              </span>
                            )}
                          </div>
                        )}

                        {selectedEvent.resource.appointment_cancelled_whatsapp_sent && (
                          <div className="space-y-1">
                            <span className="block text-red-700">
                              WhatsApp de cancelamento enviado
                              {selectedEvent.resource.appointment_cancelled_whatsapp_sent_at
                                ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_cancelled_whatsapp_sent_at)}`
                                : ''}
                            </span>

                            {selectedEvent.resource.appointment_cancelled_whatsapp_delivered && (
                              <span className="block text-red-700">
                                Cancelamento entregue
                                {selectedEvent.resource.appointment_cancelled_whatsapp_delivered_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_cancelled_whatsapp_delivered_at)}`
                                  : ''}
                              </span>
                            )}

                            {selectedEvent.resource.appointment_cancelled_whatsapp_read && (
                              <span className="block text-red-700">
                                Cancelamento lido
                                {selectedEvent.resource.appointment_cancelled_whatsapp_read_at
                                  ? ` em ${formatDateTimeBR(selectedEvent.resource.appointment_cancelled_whatsapp_read_at)}`
                                  : ''}
                              </span>
                            )}

                            {selectedEvent.resource.appointment_cancelled_whatsapp_failed && (
                              <span className="block text-red-700">
                                Falha no WhatsApp de cancelamento
                              </span>
                            )}
                          </div>
                        )}

                          </>
                        ) : (
                          <span className="text-yellow-700">
                            Módulo WhatsApp Business disponível como adicional.
                          </span>
                        )}

                        {hasWhatsAppModule && (
                          <button
                            type="button"
                            onClick={loadWhatsappMessages}
                            disabled={loadingWhatsappLogs}
                            className="mt-2 inline-flex items-center px-3 py-1.5 rounded-md border border-green-300 text-green-700 hover:bg-green-50 text-xs disabled:opacity-50"
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            {loadingWhatsappLogs ? 'Carregando...' : 'Ver mensagem enviada'}
                          </button>
                        )}

                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-medium">Status: </span>
                      {can('appointments.update') ? <select
                        value={selectedEvent.resource.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="px-2 py-1 rounded-md text-sm font-semibold border-0 outline-none cursor-pointer"
                        style={{
                          backgroundColor: getStatusColor(selectedEvent.resource.status) + '20',
                          color: getStatusColor(selectedEvent.resource.status)
                        }}
                      >
                        {STATUS.filter((status) => (
                          status.value !== 'Cancelado' ||
                          can('appointments.cancel') ||
                          selectedEvent.resource.status === 'Cancelado'
                        )).map(s => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select> : <span
                        className="px-2 py-1 rounded-md text-sm font-semibold"
                        style={{
                          backgroundColor: getStatusColor(selectedEvent.resource.status) + '20',
                          color: getStatusColor(selectedEvent.resource.status)
                        }}
                      >{selectedEvent.resource.status}</span>}
                    </div>

                    {(selectedEvent.resource.push_to_finance === true || selectedEvent.resource.invoice_id) && (() => {
                      const apt = selectedEvent.resource;
                      const stageRank = getFinancialStageRank(apt);
                      const forecastValue = Number(
                        apt.expected_amount ??
                        apt.amount ??
                        apt.produced_value ??
                        apt.invoiced_value ??
                        0
                      );
                      const invoicedValue = Number(
                        apt.invoiced_value ??
                        (apt.invoice_id ? forecastValue : 0)
                      );
                      const receivedValue = Number(apt.received_value ?? 0);
                      const hasPartialReceipt = stageRank === 4 && receivedValue > 0;

                      const pipelineSteps = [
                        { key: 'forecast', label: 'Prev.', completed: stageRank >= 1 },
                        { key: 'produced', label: 'Prod.', completed: stageRank >= 2 },
                        { key: 'invoiced', label: 'Fat.', completed: stageRank >= 3 },
                        { key: 'received', label: 'Rec.', completed: stageRank >= 5, partial: hasPartialReceipt },
                      ];

                      return (
                        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-semibold text-sky-900">Financeiro do atendimento</p>
                            <FileText className="w-5 h-5 shrink-0 text-sky-700" />
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                            <div>
                              <p className="text-xs text-sky-700">Valor</p>
                              <p className="font-semibold text-gray-900">{formatCurrencyBR(forecastValue)}</p>
                            </div>
                            {receivedValue > 0 && (
                              <div>
                                <p className="text-xs text-sky-700">Recebido</p>
                                <p className="font-semibold text-gray-900">{formatCurrencyBR(receivedValue)}</p>
                              </div>
                            )}
                            <div className="col-span-2 sm:col-span-1">
                              <p className="text-xs text-sky-700">Situação</p>
                              <p className="font-semibold text-gray-900">{getFinancialStatusText(apt)}</p>
                            </div>
                          </div>

                          <div className="flex items-start justify-between gap-1 rounded-lg border border-sky-100 bg-white px-2 py-3">
                            {pipelineSteps.map((step) => {
                              const active = step.completed || step.partial;
                              return (
                                <div key={step.key} className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center">
                                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                                    step.partial
                                      ? 'bg-amber-500 text-white'
                                      : step.completed
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-gray-200 text-gray-500'
                                  }`}>
                                    {active ? '✓' : '○'}
                                  </span>
                                  <span className={`max-w-full truncate text-[11px] font-semibold ${
                                    step.partial
                                      ? 'text-amber-800'
                                      : step.completed
                                        ? 'text-emerald-800'
                                        : 'text-gray-500'
                                  }`}>
                                    {step.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {apt.invoice_id ? (
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                              <div className="flex min-w-0 items-center gap-2 text-sm text-green-800">
                                <CheckCircle2 className="w-4 h-4 shrink-0" />
                                <span className="truncate">
                                  {apt.invoice_number
                                    ? `Fatura nº ${apt.invoice_number}`
                                    : 'Fatura vinculada'}
                                </span>
                              </div>
                              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                                {receivedValue < invoicedValue - 0.005 && (
                                  <button
                                    type="button"
                                    onClick={openPaymentModal}
                                    className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
                                  >
                                    <DollarSign className="mr-1 h-4 w-4" />
                                    Receber pagamento
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => { window.location.href = '/financeiro'; }}
                                  className="text-sm font-medium text-green-800 hover:underline"
                                >
                                  Abrir Financeiro
                                </button>
                              </div>
                            </div>
                          ) : apt.status === 'Realizado' && forecastValue > 0 ? (
                            <button
                              type="button"
                              onClick={handleGenerateAppointmentInvoice}
                              disabled={generatingInvoice}
                              className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              {generatingInvoice ? 'Gerando fatura...' : 'Gerar Fatura'}
                            </button>
                          ) : (
                            <p className="text-xs text-sky-800">
                              A fatura poderá ser gerada após o atendimento ser marcado como Realizado.
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {selectedEvent.resource.notes && (
                      <div>
                        <span className="font-medium">Observações: </span>
                        {selectedEvent.resource.notes}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 justify-end pt-4 border-t">
                      {selectedEvent.resource.patient_id &&
                      can('clinical_records.view') && (
                        <button
                          type="button"
                          onClick={handleOpenPatientRecord}
                          className="px-3 py-2 text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100"
                          title="Abrir a visão geral do prontuário"
                        >
                          <Users className="w-4 h-4 inline mr-1" />
                          Ver prontuário
                        </button>
                      )}

                      {selectedEvent.resource.patient_id &&
                      canStartAppointment(selectedEvent.resource) &&
                      can('appointments.update') &&
                      can('clinical_records.view') ? (
                        <button
                          onClick={handleCheckIn}
                          className="px-3 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                          title="Abrir prontuário e iniciar atendimento"
                        >
                          <LogIn className="w-4 h-4 inline mr-1" />
                          Check-in
                        </button>
                      ) : !selectedEvent.resource.patient_id &&
                          canUseOperationalActions(selectedEvent.resource) &&
                          can('patients.create') ? (
                        <button
                          onClick={handleCreatePatientFromGuestAppointment}
                          className="px-3 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                          title="Criar paciente a partir deste agendamento"
                        >
                          <Plus className="w-4 h-4 inline mr-1" />
                          Criar Paciente
                        </button>
                      ) : null}

                      {can('appointments.update') && <button
                        onClick={() => {
                          setEditing(selectedEvent.resource);
                          setSelectedPatientContext(null);
                          setShowEventModal(false);
                          setShowForm(true);
                        }}
                        className="px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                      >
                        <Edit className="w-4 h-4 inline mr-1" />
                        Editar
                      </button>}

                      {can('appointments.cancel') &&
                      canUseOperationalActions(selectedEvent.resource) && <button
                        onClick={handleCancelAppointment}
                        className="px-3 py-2 text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                      >
                        <X className="w-4 h-4 inline mr-1" />
                        Cancelar agendamento
                      </button>}

                      {!selectedEvent.resource?.is_block &&
                        canUseOperationalActions(selectedEvent.resource) &&
                        can('appointments.send_confirmation') && clinicSettings?.appointment_email_enabled !== false && (
                          <button
                            onClick={handleSendConfirmationEmail}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            {selectedEvent.resource?.appointment_confirmation_email_sent
                              ? 'Reenviar e-mail'
                              : 'Enviar e-mail'}
                          </button>
                        )}

                      {!selectedEvent.resource?.is_block &&
                        canUseOperationalActions(selectedEvent.resource) &&
                        can('appointments.send_confirmation') && clinicSettings?.appointment_whatsapp_enabled === true &&
                        hasWhatsAppModule && (
                          <>
                            <button
                              onClick={handleSendConfirmationWhatsApp}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              {selectedEvent.resource?.appointment_confirmation_whatsapp_sent
                                ? 'Reenviar confirmação'
                                : 'Enviar confirmação'}
                            </button>

                            <button
                              onClick={handleSendRescheduleWhatsApp}
                              className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors flex items-center"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              {selectedEvent.resource?.appointment_rescheduled_whatsapp_sent
                                ? 'Reenviar remarcação'
                                : 'Enviar remarcação'}
                            </button>

                            <button
                              onClick={handleSendCancelledWhatsApp}
                              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              {selectedEvent.resource?.appointment_cancelled_whatsapp_sent
                                ? 'Reenviar cancelamento'
                                : 'Enviar cancelamento'}
                            </button>
                          </>
                        )}

                      {!selectedEvent.resource?.is_block &&
                        canUseOperationalActions(selectedEvent.resource) &&
                        clinicSettings?.appointment_whatsapp_enabled === true &&
                        !hasWhatsAppModule && (
                          <div className="w-full rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                            Módulo WhatsApp Business não contratado.
                          </div>
                        )}

                      {can('appointments.delete') && <button
                        onClick={() => handleDelete(selectedEvent.resource.id)}
                        className="px-3 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                      >
                        <Trash2 className="w-4 h-4 inline mr-1" />
                        Excluir
                      </button>}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {showFinancialCompletionModal && selectedEvent?.resource && (
          <div
            className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !completingFinancial) setShowFinancialCompletionModal(false);
            }}
          >
            <form
              onSubmit={(event) => handleCompleteFinancial(event, false)}
              className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between border-b border-gray-200 p-5">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Finalizar atendimento</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Confirme o valor e informe se o pagamento foi recebido agora.
                  </p>
                </div>
                <button type="button" onClick={() => setShowFinancialCompletionModal(false)} disabled={completingFinancial}>
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <div className="min-h-0 space-y-4 overflow-y-auto p-5">
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                  <strong>{selectedEvent.resource.procedure || 'Atendimento odontológico'}</strong>
                  <span className="block text-sky-700">{selectedEvent.resource.patient_name}</span>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Valor do atendimento</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={financialCompletionForm.amount}
                    onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, amount: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Desconto</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={financialCompletionForm.discount_amount}
                      onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, discount_amount: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="0,00"
                    />
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <p className="text-xs text-emerald-700">Valor final</p>
                    <p className="text-lg font-bold text-emerald-900">
                      {formatCurrencyBR(Math.max(
                        Number(String(financialCompletionForm.amount || '').replace(',', '.')) -
                        (Number(String(financialCompletionForm.discount_amount || '').replace(',', '.')) || 0),
                        0
                      ))}
                    </p>
                  </div>
                </div>

                <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4">
                  <span>
                    <span className="block font-medium text-gray-900">Recebeu agora?</span>
                    <span className="block text-sm text-gray-500">Se não, criaremos uma conta a receber.</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={financialCompletionForm.receive_now}
                    onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, receive_now: e.target.checked }))}
                    className="h-5 w-5"
                  />
                </label>

                {financialCompletionForm.receive_now ? (
                  <>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Forma de pagamento</label>
                      <select
                        value={financialCompletionForm.payment_method_id}
                        onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, payment_method_id: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        required
                      >
                        {paymentMethods.map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Data do recebimento</label>
                      <input
                        type="date"
                        value={financialCompletionForm.payment_date}
                        onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, payment_date: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Forma da cobrança</label>
                      <select
                        value={financialCompletionForm.payment_type}
                        onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, payment_type: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                      >
                        <option value="single">À vista</option>
                        <option value="installment">Parcelado</option>
                      </select>
                    </div>
                    {financialCompletionForm.payment_type === 'installment' && (
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Quantidade de parcelas</label>
                        <input
                          type="number"
                          min="2"
                          max="120"
                          value={financialCompletionForm.installment_count}
                          onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, installment_count: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                          required
                        />
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        {financialCompletionForm.payment_type === 'installment' ? 'Vencimento da primeira parcela' : 'Vencimento'}
                      </label>
                      <input
                        type="date"
                        value={financialCompletionForm.due_date}
                        onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, due_date: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Observações</label>
                  <textarea
                    rows="2"
                    value={financialCompletionForm.notes}
                    onChange={(e) => setFinancialCompletionForm((previous) => ({ ...previous, notes: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Opcional"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-5 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={(event) => handleCompleteFinancial(event, true)}
                  disabled={completingFinancial}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Finalizar sem lançamento financeiro
                </button>
                <button
                  type="submit"
                  disabled={completingFinancial}
                  className="rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {completingFinancial
                    ? 'Finalizando...'
                    : financialCompletionForm.receive_now
                      ? 'Finalizar e receber'
                      : 'Finalizar e criar conta a receber'}
                </button>
              </div>
            </form>
          </div>
        )}

        {showPaymentModal && selectedEvent?.resource && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !registeringPayment) setShowPaymentModal(false);
            }}
          >
            <form
              onSubmit={handleRegisterPayment}
              className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-200 p-5">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Registrar recebimento</h3>
                  <p className="text-sm text-gray-500">
                    {selectedEvent.resource.invoice_number
                      ? `Fatura nº ${selectedEvent.resource.invoice_number}`
                      : 'Fatura do atendimento'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  disabled={registeringPayment}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="min-h-0 space-y-4 overflow-y-auto p-5">
                {(() => {
                  const apt = selectedEvent.resource;
                  const total = Number(apt.invoiced_value ?? apt.amount ?? apt.expected_amount ?? 0);
                  const received = Number(apt.received_value ?? 0);
                  const remaining = Math.max(total - received, 0);
                  return (
                    <div className="grid grid-cols-3 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Fatura</p>
                        <p className="font-semibold text-gray-900">{formatCurrencyBR(total)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Já recebido</p>
                        <p className="font-semibold text-gray-900">{formatCurrencyBR(received)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Saldo</p>
                        <p className="font-semibold text-emerald-700">{formatCurrencyBR(remaining)}</p>
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Valor recebido</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((previous) => ({ ...previous, amount: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Forma de pagamento</label>
                  <select
                    value={paymentForm.payment_method_id}
                    onChange={(e) => {
                      const method = paymentMethods.find((item) => item.id === e.target.value);
                      setPaymentForm((previous) => ({
                        ...previous,
                        payment_method_id: e.target.value,
                        payment_method_name: method?.name || '',
                      }));
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    required
                  >
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>{method.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Data do recebimento</label>
                  <input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm((previous) => ({ ...previous, payment_date: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Observações</label>
                  <textarea
                    rows="3"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm((previous) => ({ ...previous, notes: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Informação opcional sobre o recebimento"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-200 p-5">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  disabled={registeringPayment}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={registeringPayment}
                  className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  {registeringPayment ? 'Registrando...' : 'Registrar pagamento'}
                </button>
              </div>
            </form>
          </div>
        )}

        {showWhatsappMessagesModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowWhatsappMessagesModal(false);
            }}
          >
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-5 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Mensagens WhatsApp enviadas
                </h3>
                <button
                  onClick={() => setShowWhatsappMessagesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto max-h-[65vh] space-y-4">
                {whatsappLogs.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Nenhuma mensagem salva para este agendamento.
                  </p>
                ) : (
                  whatsappLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >
                      <div className="text-xs text-gray-500 mb-2">
                        Template: {log.template_name} · Tipo: {log.message_type}
                      </div>

                      <pre
                        className="whitespace-pre-wrap text-sm font-sans text-left bg-green-50 border border-green-200 rounded-2xl p-4 max-w-full"
                        style={{ textAlign: "left" }}
                      >
                        {log.message_preview || 'Mensagem não disponível.'}
                      </pre>

                      {log.sent_at && (
                        <div className="text-xs text-gray-500 mt-2">
                          Enviada em: {formatDateTimeBR(log.sent_at)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal compartilhado de criação/edição */}
        <AppointmentFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          initialData={
            editing
              ? { ...editing }
              : (() => {
                  const singleProfessional = getSingleActiveProfessional();

                  return {
                    ...(presetDateTime.date
                      ? {
                          appointment_date: presetDateTime.date,
                          start_time: presetDateTime.time,
                        }
                      : {}),
                    ...(singleProfessional
                      ? {
                          professional_id: singleProfessional.id,
                          professional_name: singleProfessional.name,
                        }
                      : {}),
                  };
                })()
          }
          patients={patients}
          treatments={treatments}
          professionals={getWritableProfessionals()}
          currentPatient={selectedPatientContext}
          toast={toast}
          onSaved={onSavedFromModal}
        />
        {showBlockForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Bloquear horário</h2>
                <button
                  onClick={() => setShowBlockForm(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateBlock} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Profissional</label>
                  <select
                    value={blockForm.professional_id}
                    onChange={(e) => {
                      const prof = getWritableProfessionals().find((p) => p.id === e.target.value);
                      setBlockForm((prev) => ({
                        ...prev,
                        professional_id: prof?.id || '',
                        professional_name: prof?.name || '',
                      }));
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {getWritableProfessionals().map((prof) => (
                      <option key={prof.id} value={prof.id}>
                        {prof.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo de bloqueio</label>
                  <select
                    value={blockForm.block_mode}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setBlockForm((prev) => ({
                        ...prev,
                        block_mode: mode,
                        end_date: prev.end_date || prev.appointment_date,
                        start_time: mode === 'time' ? prev.start_time || '08:00' : '00:00',
                        end_time: mode === 'time' ? prev.end_time || '09:00' : '23:59',
                      }));
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="time">Bloquear horário específico</option>
                    <option value="full_day">Bloquear o dia todo</option>
                    <option value="period">Bloquear período / férias / viagem</option>
                  </select>
                </div>

                <div className={blockForm.block_mode === 'period' ? 'grid grid-cols-2 gap-4' : ''}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {blockForm.block_mode === 'period' ? 'Data inicial' : 'Data'}
                    </label>
                    <input
                      type="date"
                      value={blockForm.appointment_date}
                      onChange={(e) => setBlockForm((prev) => ({
                        ...prev,
                        appointment_date: e.target.value,
                        end_date: prev.end_date || e.target.value,
                      }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                  </div>

                  {blockForm.block_mode === 'period' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Data final
                      </label>
                      <input
                        type="date"
                        value={blockForm.end_date || blockForm.appointment_date}
                        onChange={(e) => setBlockForm((prev) => ({
                          ...prev,
                          end_date: e.target.value,
                        }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                        required
                      />
                    </div>
                  )}
                </div>

                {blockForm.block_mode === 'time' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Início</label>
                      <input
                        type="time"
                        value={blockForm.start_time}
                        onChange={(e) => setBlockForm((prev) => ({
                          ...prev,
                          start_time: e.target.value,
                        }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fim</label>
                      <input
                        type="time"
                        value={blockForm.end_time}
                        onChange={(e) => setBlockForm((prev) => ({
                          ...prev,
                          end_time: e.target.value,
                        }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Motivo</label>
                  <input
                    type="text"
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="Ex.: almoço, compromisso externo, reunião..."
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowBlockForm(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-800"
                  >
                    Salvar bloqueio
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}        
      </div>
    </Layout>
  );
};

export default AgendaPage;
